/**
 * Formateo de fechas, números y otros valores comunes.
 * Locale `es-AR` por default (la app es operada desde Argentina).
 *
 * Todas las funciones aceptan valores nulos/undefined y devuelven el placeholder
 * `'—'` (em dash) para que los renders no muestren `null`/`undefined` crudos.
 */

const LOCALE = 'es-AR';
const TZ = 'America/Argentina/Buenos_Aires';
const PLACEHOLDER = '—';

/** Formatea una fecha ISO (YYYY-MM-DD o full ISO) como `DD/MM/YYYY`. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return PLACEHOLDER;
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TZ,
  }).format(d);
}

/** Formatea ISO timestamp como `DD/MM/YYYY HH:mm`. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return PLACEHOLDER;
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  }).format(d);
}

/** Formato extendido para audit trail: `DD/MM/YYYY HH:mm:ss`. */
export function fmtDateTimeLong(iso: string | null | undefined): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return PLACEHOLDER;
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: TZ,
  }).format(d);
}

/** Número con separador de miles `es-AR`. */
export function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined || isNaN(n)) return '0';
  return n.toLocaleString(LOCALE);
}

/**
 * Formato AFIP de número de remito: `R-PPVV-NNNNNNNN`.
 * Recibe el número interno (sin `R-`) y lo padea con ceros.
 */
export function fmtRemitoNumero(numero: string | null | undefined): string {
  if (!numero) return PLACEHOLDER;
  // Si ya viene con guión, no lo tocamos
  if (numero.includes('-')) return `R-${numero}`;
  // Caso solo dígitos: split 4 + 8 con padding
  if (/^\d+$/.test(numero) && numero.length === 12) {
    return `R-${numero.slice(0, 4)}-${numero.slice(4)}`;
  }
  return `R-${numero}`;
}

/** Capitaliza primera letra. Útil para labels de enums. */
export function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}
